'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { PureComponent } from 'react';
import { connect } from 'react-redux';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { OfflineEventTriggers, Networks, EVENT_CREATION_ERROR_TYPES, EVENT_CREATION_RESPONSE_MESSAGE_KEYWORDS } from 'AdsUI/lib/Constants';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import { createOfflineEvent, fetchEventList } from 'AdsUI/actions/EventSettingsActions';
import { postTimedNotification } from 'AdsUI/actions/NotificationActions';
import { usageTracker } from 'AdsUI/lib/usageTracker';
import UILock from 'ui-addon-upgrades/icons/UILock';
import { VISITOR_DATA } from 'ads-account-connection/components/VisitorDataSelection';
import { COOKIES_OPTIONS } from 'ads-account-connection/components/PrivacyAndConsentSection';
import AccountConnectionData from 'ads-account-connection/data/AccountConnectionData';
import Raven from 'raven-js';
import { Metrics } from 'AdsUI/Metrics';

class OfflineEventCreationButton extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = {
      loading: false,
      failed: false
    };
    this.onClick = this.onClick.bind(this);
  }

  hasErrors() {
    const {
      eventOptions,
      accountsWithoutBusinessManager
    } = this.props;
    const {
      currentEventTrigger,
      selectedAdAccount,
      name,
      pageViews: {
        visitorData,
        cookiesOptionSelected
      }
    } = eventOptions;

    if (!selectedAdAccount) {
      return true;
    }

    const {
      adAccount
    } = selectedAdAccount;

    if (!currentEventTrigger || !adAccount || !name) {
      return true;
    }

    switch (currentEventTrigger) {
      case OfflineEventTriggers.LIFECYCLE_STAGE_CHANGE:
        {
          const {
            selectedLifecycleStageId,
            selectedEventCategoryId,
            stageValue,
            currency,
            usedStageIds
          } = eventOptions;
          const accountWithoutBusinessManager = accountsWithoutBusinessManager.has(adAccount);
          const isInvalidAdAccount = usedStageIds.get(adAccount, []).indexOf(selectedLifecycleStageId) !== -1;
          return accountWithoutBusinessManager || isInvalidAdAccount || !selectedLifecycleStageId || !selectedEventCategoryId || !stageValue || !currency;
        }

      case OfflineEventTriggers.PAGE_VIEWS:
        {
          if (visitorData.length === 0) {
            return true;
          }

          if (cookiesOptionSelected === undefined || cookiesOptionSelected === COOKIES_OPTIONS.NONE) {
            return true;
          }

          return false;
        }

      case OfflineEventTriggers.FORM_SUBMISSION:
        {
          const {
            eventOptions: {
              selectedPixel,
              facebookBusinessExtensionAuth,
              formSubmission: {
                selectedForm,
                contactFields,
                conversionEvent,
                value
              }
            }
          } = this.props;

          if (!selectedPixel) {
            return true;
          }

          if (!selectedForm) {
            return true;
          }

          if (!conversionEvent) {
            return true;
          }

          if (adAccount.network === Networks.FACEBOOK && conversionEvent === 'PURCHASE' && value === undefined) {
            return true;
          }

          if (adAccount.network === Networks.FACEBOOK && selectedPixel.fbeinstalled === false && !facebookBusinessExtensionAuth.userToken) {
            return true;
          }

          const hasContactField = contactFields && !!Object.values(contactFields).length;

          if (adAccount.network !== Networks.FACEBOOK && !hasContactField) {
            return true;
          }

          return false;
        }

      default:
        {
          return null;
        }
    }
  }

  handleError() {
    const {
      setShowErrors
    } = this.props;
    this.setState({
      loading: false,
      failed: true
    });
    setShowErrors(true);
  }

  sendConversionEvents(adAccountId, pixelId, enabled) {
    const {
      cookiesOptionSelected,
      visitorData
    } = this.props.eventOptions.pageViews;
    const payload = {};
    VISITOR_DATA.forEach(option => {
      payload[option] = visitorData.includes(option);
    });
    AccountConnectionData.sendConversionEvents(adAccountId, pixelId, payload, enabled, cookiesOptionSelected !== COOKIES_OPTIONS.USING_COOKIE_BANNER).then(() => {
      this.setState({
        loading: false
      });
    }).catch(err => {
      this.handleError(err);
    });
  }

  setupConversionsAPI() {
    const {
      selectedPixel,
      cookiesOptionSelected,
      visitorData,
      userToken,
      externalBusinessId
    } = this.props.eventOptions.pageViews;
    const visitorPayload = {};
    VISITOR_DATA.forEach(option => {
      visitorPayload[option] = visitorData.includes(option);
    });

    if (selectedPixel && selectedPixel.fbeinstalled) {
      this.sendConversionEvents(selectedPixel.accountId, selectedPixel.id, true);
    } else {
      const payload = {
        facebookBusinessExtensionAuth: {
          externalBusinessId,
          userToken,
          adAccountId: selectedPixel.accountId,
          sendAllEventsConsent: cookiesOptionSelected !== COOKIES_OPTIONS.USING_COOKIE_BANNER
        },
        facebookPixelEventFields: visitorPayload
      };
      AccountConnectionData.setupConversionsAPI(payload).then(() => {
        this.setState({
          loading: false
        });
      }).catch(err => {
        this.handleError(err);
      });
    }
  }

  onClick() {
    const {
      eventOptions,
      toggleIsOpen,
      setShowErrors,
      setHasIncorrectBusinessManagerPermissions
    } = this.props;
    const {
      currentEventTrigger,
      selectedAdAccount
    } = eventOptions;

    if (this.hasErrors()) {
      setShowErrors(true);
      return;
    }

    this.setState({
      loading: true
    });

    switch (currentEventTrigger) {
      case OfflineEventTriggers.LIFECYCLE_STAGE_CHANGE:
        {
          const {
            selectedLifecycleStageId,
            selectedEventCategoryId,
            stageValue,
            currency,
            name,
            isEstimatedValue,
            isPrimaryForGoal,
            consentInfo
          } = eventOptions;
          const eventOpts = {
            value: stageValue,
            currencyCode: currency,
            eventTriggerType: OfflineEventTriggers.LIFECYCLE_STAGE_CHANGE,
            lifecycleStageId: selectedLifecycleStageId,
            eventName: name,
            isEstimatedValue,
            enabled: true,
            eventCategory: selectedEventCategoryId
          };
          const networkSpecificEventOpts = {
            [Networks.ADWORDS]: {
              isPrimaryForGoal
            },
            [Networks.LINKEDIN]: {
              consentInfo
            }
          };
          const {
            network,
            accountId
          } = selectedAdAccount.adAccount;
          usageTracker.track('modifyEvent', {
            action: 'Created event',
            network,
            trigger: OfflineEventTriggers.LIFECYCLE_STAGE_CHANGE,
            usingDeals: isEstimatedValue,
            lifecycleStage: selectedLifecycleStageId,
            networkStage: selectedEventCategoryId
          });
          Metrics.counter('created-event', {
            network
          }).increment();
          this.props.createOfflineEvent(network, accountId, Object.assign({}, eventOpts, {}, networkSpecificEventOpts[network])).then(() => {
            this.props.postTimedNotification({
              id: 'create-offline-event-success',
              type: 'success',
              message: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                message: "events.creation.banners.success",
                options: {
                  eventName: name
                }
              })
            }, 4000);
            this.props.fetchEventList();
            toggleIsOpen();
          }).catch(error => {
            this.setState({
              failed: true
            });
            const errorType = error.cause && error.cause.errorType || '';
            const errorMessage = error.cause && error.cause.message || '';

            if (errorType === EVENT_CREATION_ERROR_TYPES.APP_SCOPE && errorMessage.includes(EVENT_CREATION_RESPONSE_MESSAGE_KEYWORDS.MODIFY_OFFLINE_CONVERSION_DATA_SETS)) {
              setShowErrors(true);
              setHasIncorrectBusinessManagerPermissions(true);
            }
          }).finally(() => this.setState({
            loading: false
          }));
          break;
        }

      case OfflineEventTriggers.PAGE_VIEWS:
        {
          const {
            cookiesOptionSelected
          } = this.props.eventOptions.pageViews;

          if (cookiesOptionSelected === COOKIES_OPTIONS.USING_COOKIE_BANNER) {
            AccountConnectionData.hasCookieBannerInfo().then(enabled => {
              if (!enabled) {
                this.setShowCookieBannerError(true);
                return;
              }

              this.setupConversionsAPI();
            }).catch(error => {
              Raven.captureMessage('Failed to fetch cookie banner info', {
                extra: {
                  error
                }
              });
            });
          } else if (cookiesOptionSelected === COOKIES_OPTIONS.NOT_USING_COOKIE_BANNER) {
            this.setupConversionsAPI();
          }

          break;
        }

      case OfflineEventTriggers.FORM_SUBMISSION:
        {
          const {
            selectedPixel,
            formSubmission: {
              eventName,
              selectedForm: {
                id: hubSpotFormId,
                name: hubSpotFormName
              },
              value,
              currency,
              isPrimaryForGoal,
              contactFields: eventContactDataFields,
              customProperty,
              customPropertyValue,
              conversionEvent: eventCategory,
              conversionEventSetId
            },
            facebookBusinessExtensionAuth
          } = eventOptions;
          const consentInfo = customProperty && {
            consentInfo: {
              name: customProperty.value,
              value: customPropertyValue
            }
          };
          const options = Object.assign({
            eventTriggerType: OfflineEventTriggers.FORM_SUBMISSION,
            eventName,
            pixelId: selectedPixel.id,
            hubSpotFormId,
            hubSpotFormName,
            eventCategory
          }, conversionEventSetId && {
            existingEventSetId: conversionEventSetId
          }, {}, value && {
            value,
            currencyCode: currency
          }, {}, conversionEventSetId !== null && isPrimaryForGoal && {
            primaryForGoal: isPrimaryForGoal
          }, {}, consentInfo, {
            eventContactDataFields: eventContactDataFields || {}
          }, facebookBusinessExtensionAuth.userToken && {
            facebookBusinessExtensionAuth
          });
          const {
            network,
            accountId
          } = selectedAdAccount.adAccount;
          usageTracker.track('modifyEvent', {
            action: 'Created event',
            network,
            trigger: OfflineEventTriggers.FORM_SUBMISSION,
            isNew: conversionEventSetId === null
          });
          this.props.createOfflineEvent(network, accountId, Object.assign({}, options)).then(() => {
            this.props.postTimedNotification({
              id: 'create-offline-event-success',
              type: 'success',
              message: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                message: "events.creation.banners.success",
                options: {
                  eventName
                }
              })
            }, 4000);
            this.props.fetchEventList();
            toggleIsOpen();
          }).catch(error => {
            this.setState({
              failed: true
            });
            const errorType = error.cause && error.cause.errorType || '';
            const errorMessage = error.cause && error.cause.message || '';

            if (errorType === EVENT_CREATION_ERROR_TYPES.APP_SCOPE && errorMessage.includes(EVENT_CREATION_RESPONSE_MESSAGE_KEYWORDS.MODIFY_OFFLINE_CONVERSION_DATA_SETS)) {
              setShowErrors(true);
              setHasIncorrectBusinessManagerPermissions(true);
            }

            if (errorType === EVENT_CREATION_ERROR_TYPES.TERMS_OF_SERVICE_NOT_ACCEPTED) {
              setShowErrors(true);
            }
          }).finally(() => this.setState({
            loading: false
          }));
          break;
        }

      default:
        break;
    }
  }

  render() {
    const {
      locked
    } = this.props;
    const {
      loading,
      failed
    } = this.state;
    return /*#__PURE__*/_jsx(UILoadingButton, {
      use: "primary",
      disabled: locked,
      onClick: this.onClick,
      loading: loading,
      failed: failed,
      "data-test-id": "offline-event-creation-button",
      children: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "events.creation.button"
        }), locked && /*#__PURE__*/_jsx(UILock, {
          className: "m-left-1"
        })]
      })
    });
  }

}

export default connect(null, {
  createOfflineEvent,
  fetchEventList,
  postTimedNotification
})(OfflineEventCreationButton);